import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { ZagIcon } from '../icon';
import { ZagButton } from '../button/button';

/**
 * Entity action configuration for header buttons
 */
export interface EntityAction {
  /** Unique identifier for the action */
  id: string;
  /** Display label */
  label: string;
  /** Material icon name */
  icon: string;
  /** Button variant - determines styling */
  variant?: 'primary' | 'secondary' | 'danger';
  /** Whether the action is disabled */
  disabled?: boolean;
}

/**
 * EntityCardHeaderComponent - Header for entity detail pages
 *
 * Displays an avatar, title, subtitle, badges, and action buttons in a
 * consistent layout. Uses named slots for flexible content projection.
 *
 * @example Basic usage
 * ```html
 * <zag-entity-card-header
 *   [actions]="headerActions"
 *   (actionClick)="onAction($event)"
 * >
 *   <zag-avatar slot="avatar" [name]="'John Doe'" size="xl" />
 *   <span slot="title">John Doe</span>
 *   <span slot="subtitle">Software Engineer</span>
 *   <zag-badge slot="badges" color="info">IT Department</zag-badge>
 * </zag-entity-card-header>
 * ```
 *
 * @example With custom action buttons via slot
 * ```html
 * <zag-entity-card-header>
 *   <zag-avatar slot="avatar" [name]="'Jane Smith'" />
 *   <span slot="title">Jane Smith</span>
 *   <button slot="actions" zagButton="outlined" (click)="customAction()">
 *     Custom Action
 *   </button>
 * </zag-entity-card-header>
 * ```
 */
@Component({
  selector: 'zag-entity-card-header',
  imports: [ZagIcon, ZagButton],
  template: `
    <header class="entity-card-header">
      <div class="entity-card-header__avatar">
        <ng-content select="[slot=avatar]" />
      </div>

      <div class="entity-card-header__content">
        <h1 class="entity-card-header__title">
          <ng-content select="[slot=title]" />
        </h1>

        <p class="entity-card-header__subtitle">
          <ng-content select="[slot=subtitle]" />
        </p>

        <div class="entity-card-header__badges">
          <ng-content select="[slot=badges]" />
        </div>
      </div>

      <div class="entity-card-header__actions">
        @for (action of actions(); track action.id) {
          <button
            zagButton="outlined"
            [zagType]="getButtonType(action)"
            [disabled]="action.disabled ?? false"
            (click)="onActionClick(action)"
            type="button"
          >
            <span [zagIcon]="action.icon"></span>
            <span class="entity-card-header__action-label">{{ action.label }}</span>
          </button>
        }
        <ng-content select="[slot=actions]" />
      </div>
    </header>
  `,
  styleUrl: './entity-card-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-entity-card-header',
  },
})
export class EntityCardHeaderComponent {
  /** Actions to display as buttons in the header */
  readonly actions = input<EntityAction[]>([]);

  /** Emits when an action button is clicked */
  readonly actionClick = output<EntityAction>();

  /**
   * Map action variant to button type
   */
  protected getButtonType(action: EntityAction): 'primary' | 'secondary' | 'error' {
    switch (action.variant) {
      case 'danger':
        return 'error';
      case 'secondary':
        return 'secondary';
      case 'primary':
      default:
        return 'primary';
    }
  }

  /**
   * Handle action button click
   */
  protected onActionClick(action: EntityAction): void {
    if (!action.disabled) {
      this.actionClick.emit(action);
    }
  }
}
